export type EventCategory =
  | 'point'
  | 'foul'
  | 'turnover'
  | 'timeout'
  | 'injury'
  | 'general'

export type TeamSide = 'home' | 'away' | 'neutral'

export interface TimelineEvent {
  id: string
  quarter: 'Q1' | 'Q2' | 'Q3' | 'Q4'
  time: string
  minute: number
  team: TeamSide
  category: EventCategory
  event: string
  player?: string
  detail: string
  score?: string
}

export interface ScoringRow {
  player: string
  team: TeamSide
  /** points scored keyed by absolute game minute (1-40) */
  minutes: Record<number, number>
}

export const teams = {
  home: {
    name: 'New York Knicks',
    short: 'NYK',
    quarterScores: [28, 31, 24, 29],
    total: 112,
  },
  away: {
    name: 'Los Angeles Lakers',
    short: 'LAL',
    quarterScores: [24, 28, 30, 26],
    total: 108,
  },
}

export const quarterLabels: Record<string, string> = {
  Q1: 'First Quarter',
  Q2: 'Second Quarter',
  Q3: 'Third Quarter',
  Q4: 'Fourth Quarter',
}

export const categoryLabels: Record<EventCategory, string> = {
  point: 'Points',
  foul: 'Fouls',
  turnover: 'Turnovers',
  timeout: 'Timeouts',
  injury: 'Injuries',
  general: 'General',
}

export const events: TimelineEvent[] = [
  // First quarter
  {
    id: 'q1-1',
    quarter: 'Q1',
    time: '10:00',
    minute: 1,
    team: 'neutral',
    category: 'general',
    event: 'Game Start',
    detail: 'Tip off won by New York Knicks',
    score: '0 - 0',
  },
  {
    id: 'q1-2',
    quarter: 'Q1',
    time: '9:42',
    minute: 1,
    team: 'away',
    category: 'point',
    event: '2PT Made',
    player: 'LeBron James',
    detail: 'Jump shot',
    score: '0 - 2',
  },
  {
    id: 'q1-3',
    quarter: 'Q1',
    time: '9:15',
    minute: 2,
    team: 'home',
    category: 'point',
    event: '3PT Made',
    player: 'Jalen Brunson',
    detail: 'Step back jump shot',
    score: '3 - 2',
  },
  {
    id: 'q1-4',
    quarter: 'Q1',
    time: '8:58',
    minute: 2,
    team: 'away',
    category: 'turnover',
    event: 'Turnover',
    player: 'Anthony Davis',
    detail: 'Bad pass, stolen by Josh Hart',
  },
  {
    id: 'q1-5',
    quarter: 'Q1',
    time: '8:45',
    minute: 2,
    team: 'home',
    category: 'point',
    event: '2PT Made',
    player: 'Julius Randle',
    detail: 'Driving layup',
    score: '5 - 2',
  },
  {
    id: 'q1-6',
    quarter: 'Q1',
    time: '7:22',
    minute: 3,
    team: 'away',
    category: 'point',
    event: '2PT Made',
    player: 'Anthony Davis',
    detail: 'Alley oop dunk',
    score: '5 - 4',
  },
  {
    id: 'q1-7',
    quarter: 'Q1',
    time: '6:45',
    minute: 4,
    team: 'home',
    category: 'foul',
    event: 'Personal Foul',
    player: 'Mitchell Robinson',
    detail: '1st team foul of the quarter',
  },
  {
    id: 'q1-8',
    quarter: 'Q1',
    time: '5:30',
    minute: 5,
    team: 'away',
    category: 'general',
    event: 'Substitution',
    player: "D'Angelo Russell",
    detail: 'Austin Reaves checks out',
  },
  {
    id: 'q1-9',
    quarter: 'Q1',
    time: '4:15',
    minute: 6,
    team: 'home',
    category: 'point',
    event: '3PT Made',
    player: 'Donte DiVincenzo',
    detail: 'Corner three',
    score: '14 - 11',
  },
  {
    id: 'q1-10',
    quarter: 'Q1',
    time: '3:00',
    minute: 7,
    team: 'away',
    category: 'timeout',
    event: 'Timeout',
    detail: 'Los Angeles Lakers full timeout',
  },
  {
    id: 'q1-11',
    quarter: 'Q1',
    time: '1:20',
    minute: 9,
    team: 'away',
    category: 'injury',
    event: 'Injury',
    player: 'Jarred Vanderbilt',
    detail: 'Left ankle, returned to the bench',
  },
  {
    id: 'q1-12',
    quarter: 'Q1',
    time: '0:00',
    minute: 10,
    team: 'neutral',
    category: 'general',
    event: 'End of Quarter',
    detail: 'End of 1st Quarter: Knicks 28, Lakers 24',
    score: '28 - 24',
  },
  // Second quarter
  {
    id: 'q2-1',
    quarter: 'Q2',
    time: '9:38',
    minute: 11,
    team: 'home',
    category: 'point',
    event: '2PT Made',
    player: 'Josh Hart',
    detail: 'Cutting layup',
    score: '30 - 24',
  },
  {
    id: 'q2-2',
    quarter: 'Q2',
    time: '8:52',
    minute: 12,
    team: 'away',
    category: 'point',
    event: '3PT Made',
    player: 'Austin Reaves',
    detail: 'Pull up three',
    score: '30 - 27',
  },
  {
    id: 'q2-3',
    quarter: 'Q2',
    time: '7:41',
    minute: 13,
    team: 'home',
    category: 'turnover',
    event: 'Turnover',
    player: 'Julius Randle',
    detail: 'Offensive foul, charge',
  },
  {
    id: 'q2-4',
    quarter: 'Q2',
    time: '6:05',
    minute: 14,
    team: 'away',
    category: 'foul',
    event: 'Shooting Foul',
    player: 'Rui Hachimura',
    detail: 'Brunson to the line for two',
  },
  {
    id: 'q2-5',
    quarter: 'Q2',
    time: '4:58',
    minute: 16,
    team: 'home',
    category: 'point',
    event: '3PT Made',
    player: 'Donte DiVincenzo',
    detail: 'Transition three',
    score: '44 - 36',
  },
  {
    id: 'q2-6',
    quarter: 'Q2',
    time: '3:22',
    minute: 17,
    team: 'home',
    category: 'timeout',
    event: 'Timeout',
    detail: 'New York Knicks short timeout',
  },
  {
    id: 'q2-7',
    quarter: 'Q2',
    time: '1:44',
    minute: 19,
    team: 'away',
    category: 'point',
    event: '2PT Made',
    player: 'LeBron James',
    detail: 'Driving dunk',
    score: '55 - 50',
  },
  {
    id: 'q2-8',
    quarter: 'Q2',
    time: '0:00',
    minute: 20,
    team: 'neutral',
    category: 'general',
    event: 'End of Half',
    detail: 'Halftime: Knicks 59, Lakers 52',
    score: '59 - 52',
  },
  // Third quarter
  {
    id: 'q3-1',
    quarter: 'Q3',
    time: '9:30',
    minute: 21,
    team: 'away',
    category: 'point',
    event: '3PT Made',
    player: "D'Angelo Russell",
    detail: 'Wing three off the pick',
    score: '59 - 55',
  },
  {
    id: 'q3-2',
    quarter: 'Q3',
    time: '8:12',
    minute: 22,
    team: 'home',
    category: 'foul',
    event: 'Personal Foul',
    player: 'Josh Hart',
    detail: 'Reach in on the perimeter',
  },
  {
    id: 'q3-3',
    quarter: 'Q3',
    time: '7:05',
    minute: 23,
    team: 'away',
    category: 'point',
    event: '2PT Made',
    player: 'Anthony Davis',
    detail: 'Turnaround hook',
    score: '63 - 61',
  },
  {
    id: 'q3-4',
    quarter: 'Q3',
    time: '5:48',
    minute: 25,
    team: 'home',
    category: 'turnover',
    event: 'Turnover',
    player: 'Jalen Brunson',
    detail: 'Lost ball, stolen by Reaves',
  },
  {
    id: 'q3-5',
    quarter: 'Q3',
    time: '4:30',
    minute: 26,
    team: 'home',
    category: 'timeout',
    event: 'Timeout',
    detail: 'New York Knicks full timeout after 8-0 run',
  },
  {
    id: 'q3-6',
    quarter: 'Q3',
    time: '3:10',
    minute: 27,
    team: 'home',
    category: 'injury',
    event: 'Injury',
    player: 'Mitchell Robinson',
    detail: 'Right knee soreness, questionable to return',
  },
  {
    id: 'q3-7',
    quarter: 'Q3',
    time: '1:02',
    minute: 29,
    team: 'home',
    category: 'point',
    event: '3PT Made',
    player: 'Jalen Brunson',
    detail: 'Deep three to stop the run',
    score: '81 - 80',
  },
  {
    id: 'q3-8',
    quarter: 'Q3',
    time: '0:00',
    minute: 30,
    team: 'neutral',
    category: 'general',
    event: 'End of Quarter',
    detail: 'End of 3rd Quarter: Knicks 83, Lakers 82',
    score: '83 - 82',
  },
  // Fourth quarter
  {
    id: 'q4-1',
    quarter: 'Q4',
    time: '8:40',
    minute: 32,
    team: 'away',
    category: 'point',
    event: '3PT Made',
    player: 'Austin Reaves',
    detail: 'Catch and shoot three',
    score: '85 - 87',
  },
  {
    id: 'q4-2',
    quarter: 'Q4',
    time: '6:15',
    minute: 34,
    team: 'home',
    category: 'point',
    event: '2PT Made',
    player: 'Julius Randle',
    detail: 'Post up fadeaway',
    score: '95 - 94',
  },
  {
    id: 'q4-3',
    quarter: 'Q4',
    time: '4:02',
    minute: 36,
    team: 'away',
    category: 'foul',
    event: 'Technical Foul',
    player: 'Anthony Davis',
    detail: 'Arguing an out of bounds call',
  },
  {
    id: 'q4-4',
    quarter: 'Q4',
    time: '2:28',
    minute: 38,
    team: 'away',
    category: 'timeout',
    event: 'Timeout',
    detail: 'Los Angeles Lakers final timeout',
  },
  {
    id: 'q4-5',
    quarter: 'Q4',
    time: '1:11',
    minute: 39,
    team: 'home',
    category: 'turnover',
    event: 'Turnover',
    player: 'Donte DiVincenzo',
    detail: 'Travelling',
  },
  {
    id: 'q4-6',
    quarter: 'Q4',
    time: '0:05',
    minute: 40,
    team: 'home',
    category: 'point',
    event: '2PT Made',
    player: 'Jalen Brunson',
    detail: 'Game winning jump shot',
    score: '112 - 108',
  },
  {
    id: 'q4-7',
    quarter: 'Q4',
    time: '0:00',
    minute: 40,
    team: 'neutral',
    category: 'general',
    event: 'Game End',
    detail: 'Final: Knicks 112, Lakers 108',
    score: '112 - 108',
  },
]

const buildMinutes = (pairs: [number, number][]): Record<number, number> =>
  pairs.reduce<Record<number, number>>((acc, [minute, points]) => {
    acc[minute] = (acc[minute] || 0) + points
    return acc
  }, {})

export const scoringRows: ScoringRow[] = [
  {
    player: 'Donte DiVincenzo',
    team: 'home',
    minutes: buildMinutes([
      [6, 3],
      [8, 2],
      [16, 3],
      [18, 2],
      [24, 3],
      [33, 3],
      [37, 2],
    ]),
  },
  {
    player: 'Isaiah Hartenstein',
    team: 'home',
    minutes: buildMinutes([
      [15, 2],
      [28, 2],
      [35, 1],
    ]),
  },
  {
    player: 'Jalen Brunson',
    team: 'home',
    minutes: buildMinutes([
      [2, 3],
      [4, 2],
      [7, 2],
      [12, 3],
      [17, 2],
      [22, 4],
      [29, 3],
      [31, 2],
      [34, 3],
      [36, 2],
      [38, 6],
      [40, 2],
    ]),
  },
  {
    player: 'Josh Hart',
    team: 'home',
    minutes: buildMinutes([
      [11, 2],
      [21, 3],
      [26, 2],
      [32, 3],
      [39, 2],
    ]),
  },
  {
    player: 'Julius Randle',
    team: 'home',
    minutes: buildMinutes([
      [2, 2],
      [9, 4],
      [13, 3],
      [19, 2],
      [23, 2],
      [27, 4],
      [34, 2],
      [37, 3],
      [40, 2],
    ]),
  },
  {
    player: 'Mitchell Robinson',
    team: 'home',
    minutes: buildMinutes([
      [5, 2],
      [14, 2],
      [25, 4],
    ]),
  },
  {
    player: 'Anthony Davis',
    team: 'away',
    minutes: buildMinutes([
      [3, 2],
      [6, 2],
      [12, 4],
      [16, 3],
      [23, 2],
      [26, 4],
      [30, 2],
      [33, 4],
      [36, 2],
      [39, 2],
    ]),
  },
  {
    player: 'Austin Reaves',
    team: 'away',
    minutes: buildMinutes([
      [12, 3],
      [18, 2],
      [24, 2],
      [32, 3],
      [35, 3],
      [38, 2],
    ]),
  },
  {
    player: "D'Angelo Russell",
    team: 'away',
    minutes: buildMinutes([
      [4, 3],
      [11, 2],
      [21, 3],
      [25, 3],
      [29, 2],
      [34, 3],
      [37, 2],
    ]),
  },
  {
    player: 'Gabe Vincent',
    team: 'away',
    minutes: buildMinutes([[20, 3]]),
  },
  {
    player: 'LeBron James',
    team: 'away',
    minutes: buildMinutes([
      [1, 2],
      [5, 3],
      [8, 2],
      [14, 2],
      [19, 2],
      [22, 3],
      [27, 4],
      [31, 2],
      [35, 2],
      [38, 4],
      [40, 2],
    ]),
  },
  {
    player: 'Rui Hachimura',
    team: 'away',
    minutes: buildMinutes([
      [9, 2],
      [17, 3],
      [28, 2],
      [33, 3],
    ]),
  },
]

export const rowTotal = (row: ScoringRow): number =>
  Object.values(row.minutes).reduce((sum, value) => sum + value, 0)
